import { useQuery } from 'react-query';
import * as React from 'react';
import { trackingUrls } from 'utils/apiUrls';
import { SomaticScoreResponse } from 'pages/Dashboard/types/waypoints.types';
import { fetchSomaticScore } from 'pages/Dashboard/services/tracking.services';
import { useHttp } from 'hooks/use-fetch';

interface UseSomaticScoreDataProps {
  providerId: string | undefined;
  patientId: string | undefined;
  timeFrame: string;
  isNewVersionEnabled: boolean;
}

function useSomaticScoreData({
  providerId,
  patientId,
  timeFrame,
  isNewVersionEnabled,
}: UseSomaticScoreDataProps) {
  const { http } = useHttp();
  const fetchSomaticScoreResult = useQuery(
    [
      `${trackingUrls.somaticScore.queryUrl}-${timeFrame}`,
      providerId,
      patientId,
      timeFrame,
    ],
    {
      queryFn: fetchSomaticScore(http.get),
      enabled: !!providerId && !!patientId && isNewVersionEnabled,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
    },
  );

  const somaticScoreTimeSeriesData = React.useMemo(() => {
    const data = (fetchSomaticScoreResult?.data as SomaticScoreResponse)?.data || [];
    return data;
  }, [fetchSomaticScoreResult?.data]);

  const somaticScoreTimeSeriesAvgData = React.useMemo(() => {
    const data = (fetchSomaticScoreResult?.data as SomaticScoreResponse)?.absoluteAvgValue || 0;
    return data;
  }, [fetchSomaticScoreResult?.data]);

  const isSomaticScoresDataLoading = React.useMemo(
    () => fetchSomaticScoreResult?.isFetching,
    [fetchSomaticScoreResult?.isFetching],
  );


  return {
    somaticScoreTimeSeriesData,
    somaticScoreTimeSeriesAvgData,
    isSomaticScoresDataLoading,
  };
}

export default useSomaticScoreData;
